import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Drawer } from '@arco-design/web-react'
import { IconDashboard, IconHome } from '@arco-design/web-react/icon'
import { Menu as MenuIcon } from 'lucide-react'
import PublicPageShell from '@/components/Public/PublicPageShell'
import TutorialContent from '@/components/Tutorials/TutorialContent'
import { useAuthStore } from '@/stores/authStore'
import { tutorialTools } from './tutorialData'
import './style.css'

export default function Docs() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated)
  const [activeToolId, setActiveToolId] = useState(tutorialTools[0]?.id ?? '')
  const [mobileNavOpen, setMobileNavOpen] = useState(false)

  const activeTool = tutorialTools.find((tool) => tool.id === activeToolId) ?? tutorialTools[0]

  const handleSelectTool = (toolId: string) => {
    setActiveToolId(toolId)
    setMobileNavOpen(false)
  }

  const handleStartFromHome = () => {
    navigate('/', { state: { openAuth: true, authView: 'register' } })
  }

  const sidebarNav = (
    <nav className="docs-sidebar-nav" aria-label={t('docs.sidebarTitle')}>
      {tutorialTools.map((tool) => (
        <button
          key={tool.id}
          type="button"
          className={`docs-nav-item ${activeToolId === tool.id ? 'is-active' : ''}`}
          onClick={() => handleSelectTool(tool.id)}
          aria-current={activeToolId === tool.id ? 'page' : undefined}
        >
          <span className="docs-nav-item-name">{tool.name}</span>
          <span className="docs-nav-item-tagline">{tool.tagline}</span>
        </button>
      ))}
    </nav>
  )

  const drawerQuickActions = (
    <div className="docs-drawer-actions">
      <button type="button" className="public-btn-ghost w-full justify-start" onClick={() => navigate('/')}>
        <IconHome />
        {t('common.backHome')}
      </button>
      {isAuthenticated ? (
        <button type="button" className="public-btn-primary w-full justify-center" onClick={() => navigate('/console')}>
          <IconDashboard />
          {t('common.enterConsole')}
        </button>
      ) : (
        <button type="button" className="public-btn-primary w-full justify-center" onClick={handleStartFromHome}>
          {t('landing.getStarted')}
        </button>
      )}
    </div>
  )

  return (
    <PublicPageShell contentWidth="full" mainClassName="docs-page-main">
      <Drawer
        visible={mobileNavOpen}
        placement="left"
        width={300}
        title={t('docs.sidebarTitle')}
        footer={null}
        onCancel={() => setMobileNavOpen(false)}
        className="docs-mobile-drawer lg:hidden"
      >
        <p className="text-sm text-[#64748B] mb-4">{t('docs.sidebarDescription')}</p>
        {drawerQuickActions}
        <div className="docs-drawer-divider" />
        {sidebarNav}
      </Drawer>

      <div className="docs-shell">
        <aside className="docs-sidebar hidden lg:block">
          <h2 className="docs-sidebar-title">{t('docs.sidebarTitle')}</h2>
          <p className="docs-sidebar-desc">{t('docs.sidebarDescription')}</p>
          {sidebarNav}
        </aside>

        <section className="docs-content">
          <div className="docs-mobile-toolbar lg:hidden">
            <button
              type="button"
              aria-label={t('common.openNavMenu')}
              className="docs-mobile-menu-btn"
              onClick={() => setMobileNavOpen(true)}
            >
              <MenuIcon size={18} />
            </button>
            <span className="docs-mobile-toolbar-title">{t('docs.sidebarTitle')}</span>
          </div>

          <div className="docs-mobile-tabs lg:hidden">
            <div className="docs-mobile-tabs-scroll">
              {tutorialTools.map((tool) => (
                <button
                  key={`mobile-${tool.id}`}
                  type="button"
                  className={`docs-nav-item docs-nav-item-compact ${activeToolId === tool.id ? 'is-active' : ''}`}
                  onClick={() => handleSelectTool(tool.id)}
                  aria-current={activeToolId === tool.id ? 'page' : undefined}
                >
                  {tool.name}
                </button>
              ))}
            </div>
          </div>

          <div className="docs-content-card">
            <TutorialContent tool={activeTool} />
          </div>
        </section>
      </div>
    </PublicPageShell>
  )
}
